import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { InsertTaxonomyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Input for the compound taxonomy insert (RSP-6488). The legacy `.graphql`
 * hard-coded these literals because the insert had no form; the tRPC input
 * accepts them as variables so the write is form-ready. `OrgKey` and audit
 * columns are derived server-side from the caller — never sent from here.
 */
export interface InsertTaxonomyInput {
  Description: string;
  Common: JSONB;
  Library: JSONB;
  Rating: JSONB;
  Taxonomy: JSONB;
  InternalAuditRating: JSONB;
  OversightRating: JSONB;
  Locale: string;
  OrgName: string;
}

/**
 * Reshapes the tRPC insert response to the GraphQL `InsertTaxonomy` mutation
 * shape so consumers branching on the result never learn which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  Id: string;
}): InsertTaxonomyMutation => ({
  insert_taxonomy_one: {
    __typename: 'taxonomy',
    Id: data.Id,
  },
});

export const useInsertTaxonomyTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.taxonomy.insert.mutationOptions({
      onSuccess: async () => {
        // Mirror the legacy Apollo refetch: the settings tab reads the version
        // history via taxonomyAudit; a new taxonomy adds a version + bumps the
        // org-usage count carried on every audit row.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.taxonomy.taxonomyAudit.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.taxonomy.taxonomyByLocale.queryKey(),
        });
      },
    }),
  });

  return {
    insertTaxonomy: async (
      input: InsertTaxonomyInput
    ): Promise<InsertTaxonomyMutation> => {
      const result = await mutation.mutateAsync(input);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
